import { ApolloError } from 'apollo-client'
import * as React from 'react'
import { Mutation, MutationFunction, MutationResult } from 'react-apollo'

import { CredentialsFields } from 'main/gql/credentials'
import {
  CREATE_DIRECTOR_USER_MUTATION,
  CreateDirectorUser,
  CreateDirectorUserVariables,
  USER_QUERY,
} from 'main/gql/user'
import { Alert, NativeLink, PhoneTextField, PostalCodeTextField, Text } from 'main/ui'
import { UserSignUpSource } from 'main/util/user'
import {
  ExistingUser,
  FormContainer,
  LicensedField,
  Required,
  Terms,
  UserFields,
} from '../../common'
import {
  BlockContainer,
  DirectorFieldGroupContainer,
  RightTextContainer,
  SmallTextContainer,
  StyledButton,
  TextualContainer,
  UserFieldContainer,
} from './DirectorSignUpForm.styles'

interface IProps {
  onComplete: (credentials: CredentialsFields) => void
  onRequestLogin: () => void
  onRequestParentSignUp: () => void
}

interface IData {
  email: string
  password: string
  firstName: string
  lastName: string
  phone: string
  postalCode: string
  isLicensed: boolean
}

interface IValidation {
  email?: string
  password?: string
  firstName?: string
  lastName?: string
  phone?: string
  postalCode?: string
}

interface IState {
  data: IData
  validationHints: IValidation
  error: boolean
}

export default class DirectorSignUpForm extends React.Component<IProps, IState> {
  public state: IState = {
    data: {
      email: '',
      firstName: '',
      isLicensed: false,
      lastName: '',
      password: '',
      phone: '',
      postalCode: '',
    },
    error: false,
    validationHints: {},
  }

  public render() {
    const { onComplete } = this.props

    return (
      <Mutation<CreateDirectorUser, CreateDirectorUserVariables>
        mutation={CREATE_DIRECTOR_USER_MUTATION}
        onCompleted={({ createDirectorUser }) => {
          const credentials = createDirectorUser && createDirectorUser.credentials

          if (!credentials) {
            throw new Error('no credentials')
          } else {
            onComplete(credentials)
          }
        }}
        onError={this.handleError}
        refetchQueries={[{ query: USER_QUERY }]}
      >
        {this.renderForm.bind(this)}
      </Mutation>
    )
  }

  public renderForm(
    mutate: MutationFunction<CreateDirectorUser, CreateDirectorUserVariables>,
    { loading }: MutationResult<CreateDirectorUser>,
  ) {
    const { onRequestLogin, onRequestParentSignUp } = this.props
    const { data, error, validationHints } = this.state

    return (
      <FormContainer
        onSubmit={event => {
          event.preventDefault()
          this.handleSubmit(mutate)
        }}
      >
        <div>
          <Text>Looking to sign up as a parent instead?&nbsp;</Text>
          <NativeLink onClick={() => onRequestParentSignUp()}>Click here.</NativeLink>
        </div>

        <TextualContainer>
          <Text>
            Launch and fill your program with Wonderschool's step-by-step guidance and support.
          </Text>
        </TextualContainer>

        {error && this.renderError()}

        <RightTextContainer>
          <Required />
        </RightTextContainer>

        <UserFieldContainer>
          <UserFields
            data={data}
            validationHints={validationHints}
            onChange={changes => this.handleChange(changes)}
          />
        </UserFieldContainer>

        <DirectorFieldGroupContainer>
          <PhoneTextField
            label="Mobile Phone Number"
            placeholder="Enter your phone number"
            value={data.phone}
            onChange={phone => this.handleChange({ phone })}
            validationHint={validationHints.phone}
            required
          />

          <PostalCodeTextField
            label="Zip Code"
            placeholder="Enter your zip code"
            value={data.postalCode}
            onChange={postalCode => this.handleChange({ postalCode })}
            validationHint={validationHints.postalCode}
            required
          />
        </DirectorFieldGroupContainer>

        <SmallTextContainer>
          <LicensedField
            value={data.isLicensed}
            onChange={(isLicensed: boolean) => this.handleChange({ isLicensed })}
          />
        </SmallTextContainer>

        <StyledButton
          kind="regular"
          onClick={() => this.handleSubmit(mutate)}
          disabled={loading}
          full
          data-pendo="directorSignUpButton"
        >
          {loading ? 'Signing up...' : 'Sign up'}
        </StyledButton>

        <SmallTextContainer>
          <Terms />
        </SmallTextContainer>

        <BlockContainer>
          <ExistingUser onRequestLogin={onRequestLogin} />
        </BlockContainer>
      </FormContainer>
    )
  }

  private renderError() {
    return (
      <TextualContainer>
        <Alert kind="danger" onDismiss={() => this.setState({ error: false })}>
          Something went wrong!
        </Alert>
      </TextualContainer>
    )
  }

  private handleChange = (changes: Partial<IData>) => {
    this.setState(prevState => ({
      data: { ...prevState.data, ...changes },
    }))
  }

  private handleSubmit = (
    mutate: MutationFunction<CreateDirectorUser, CreateDirectorUserVariables>,
  ) => {
    if (!this.validate()) return

    const { data } = this.state
    const { email, isLicensed, firstName, lastName, password, phone, postalCode } = data

    mutate({
      variables: {
        user: {
          directorTosAgreed: true,
          email: email.trim(),
          family: {},
          firstName,
          isLicensed,
          lastName,
          password,
          phone,
          postalCode: postalCode.trim(),
          signUpSource: UserSignUpSource.DirectorSignUpModal,
        },
      },
    })
  }

  private handleError = (error: ApolloError) => {
    const validationHints: IValidation = {}
    const messages = error.graphQLErrors.map(e => e.message)

    // HACK (alex): We will want to figure out a more robust method of translating server
    // validation errors to client fields.

    if (messages.includes('email has already been taken')) {
      validationHints.email = 'Email has already been taken'
    }

    if (messages.includes('email is invalid')) {
      validationHints.email = 'Please enter a valid email'
    }

    if (messages.includes('email has invalid format')) {
      validationHints.email = 'Please enter a valid email'
    }

    if (messages.includes('first_name cannot include an @ symbol')) {
      validationHints.firstName = 'First name cannot have an @ symbol'
    }

    if (messages.includes('last_name cannot include an @ symbol')) {
      validationHints.lastName = 'Last name cannot have an @ symbol'
    }

    this.setState({ error: !Object.keys(validationHints).length, validationHints })
  }

  private validate() {
    const { data } = this.state
    const { email, password, firstName, lastName, phone, postalCode } = data

    const validationHints = {
      email: email ? '' : 'Please enter an email',
      firstName: firstName ? '' : 'Please enter a first name',
      lastName: lastName ? '' : 'Please enter a last name',
      password: password ? '' : 'Please enter a password',
      phone: phone ? '' : 'Please enter a phone number',
      postalCode: postalCode ? '' : 'Please enter a zip code',
    }

    if (password && password.length < 2) {
      validationHints.password = 'Please enter a longer password'
    }

    if (postalCode && postalCode.length !== 5) {
      validationHints.postalCode = 'Please enter a valid zip code'
    }

    this.setState({ error: false, validationHints })

    return (
      !validationHints.email &&
      !validationHints.firstName &&
      !validationHints.lastName &&
      !validationHints.password &&
      !validationHints.phone &&
      !validationHints.postalCode
    )
  }
}
